import { ChatMessage } from "../chat-components/chat-message/ChatMessage";
import { SubmitInput } from "../chat-components/SubmitInput";
import { useUserConversationChat } from "./useUserConversationChat";
import { SearchUserSchemaType } from "@/components/modules/user/api/schemas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, MessageSquareReply, RefreshCcw, Trash2, X } from "lucide-react";
import React from "react";
import { ChatHeader } from "../chat-components/chat-header/ChatHeader";
import { RealEstateConversationAtributesSchemaType } from "../../api/schemas";
import type { ActionItem } from "@/components/molecules/context-actions/ContextActions";
import { usePointerCoarse } from "@/hooks/usePointerCoarse";
import { Bio } from "./components/Bio";
import { cn } from "@/lib/utils";
import { formatMessageTimestamp } from "@/lib/time";
import { ROUTE_PATHS } from "@/constants/routes";
import { useTranslations } from "next-intl";
type Props = {
  conversationId?: string;
  recipientId?: string;
  user?: SearchUserSchemaType;
  realEstateId?: string;
  realEstateConversationAtributes?: RealEstateConversationAtributesSchemaType;
  isFriend?: boolean;
};
export function UserConversationChat({
  conversationId,
  recipientId,
  user,
  realEstateId,
  realEstateConversationAtributes,
  isFriend
}: Props) {
  const tChat = useTranslations("modules.chat");
  const isTouch = usePointerCoarse();
  const {
    inputValue,
    setInputValue,
    replyTo,
    setReplyTo,
    pendingImages,
    addImages,
    removeImage,
    scrollToMessage,
    conversationData,
    sessionData,
    isOtherUserTyping,
    router,
    closeModal,
    handleSubmit,
    deleteMessage,
    messages,
    inputRef,
    messagesEndRef,
    conversationRef,
    chatRootRef,
    keyboardOpen,
    onInputFocus,
    onInputBlur,
    isFetchingNextPage,
    isLoadingConversation,
    isReady,
    isAtBottom,
    handleScrollToBottom,
    unreadCount,
    firstUnreadMessageId,
    firstUnreadOtherUserMessageId,
    newMessagesCount,
    isEmailVerified,
    hasUserAnsweredAfterFirstMessage
  } = useUserConversationChat({
    conversationId,
    recipientId,
    realEstateId,
    isFriend
  });

  // Loading an existing conversation: reserve the full dialog size and show
  // skeletons so the modal doesn't open small and then jump to full size.
  if (isLoadingConversation && messages.length === 0) {
    return <div className="relative flex flex-col w-full md:w-[600px] lg:w-[700px] h-full md:h-[600px] lg:h-[800px]">
        <div className="border-b">
          <div className="relative flex items-center gap-2 sm:gap-4 p-0.5 sm:p-4 ml-10 sm:ml-0">
            <Skeleton className="w-12 h-12 rounded-full shrink-0" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-4 w-32 rounded" />
              <Skeleton className="h-3 w-20 rounded" />
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
          {Array.from({
          length: 8
        }).map((_, i) => {
          const mine = i % 3 === 0;
          return <div key={i} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                <Skeleton className={cn("h-10 rounded-2xl", i % 2 === 0 ? "w-2/5" : "w-3/5")} />
              </div>;
        })}
        </div>
      </div>;
  }
  if (messages.length === 0 && !recipientId) return null;
  const otherUserLastReadAt = conversationData?.pages[0]?.data?.conversation.otherUserLastReadAt;
  const firstUnreadByOtherUserIndex = firstUnreadOtherUserMessageId ? messages.findIndex(m => m.id === firstUnreadOtherUserMessageId) : -1;
  const separatorIndex = firstUnreadMessageId ? messages.findIndex(m_0 => m_0.id === firstUnreadMessageId) - 1 : -1;
  const bio = conversationData?.pages[0]?.data?.conversation?.otherUser?.bio;
  return <div ref={chatRootRef} className="relative flex flex-col w-full md:w-[600px] lg:w-[700px] h-full md:h-[600px] lg:h-[800px]" data-sentry-component="UserConversationChat" data-sentry-source-file="UserConversationChat.tsx">
      <ChatHeader realEstateConversationAtributes={realEstateConversationAtributes} user={user} isOtherUserTyping={isOtherUserTyping} conversation={conversationData?.pages[0].data?.conversation} userName={conversationData?.pages[0].data?.conversation.otherUser.name || user?.name} data-sentry-element="ChatHeader" data-sentry-source-file="UserConversationChat.tsx" />
      {bio && <Bio bio={bio} />}
      <div ref={conversationRef} className={cn("relative flex flex-col flex-1 overflow-y-auto scrollbar-none rounded-sm transition-opacity duration-150", !isReady && messages.length > 0 ? "opacity-0" : "opacity-100")}>
        {isFetchingNextPage && <div className={cn("absolute left-1/2 text-foreground-heading text-xs border  -translate-x-1/2  z-20 bg-card/80 p-2 rounded-full shadow-md", bio ? "top-11" : "top-2")}>
            {tChat("loadingMessages")}
          </div>}
        {!isEmailVerified && <div className={cn("absolute left-1/2 flex flex-col gap-2 p-3 text-foreground-heading text-xs border top-1/2 -translate-x-1/2  z-20 bg-card/80 rounded-full shadow-md text-center w-[320px] max-w-[90%]")}>
            <p className="">{tChat("validateEmailMessage")}</p>
            <span className="underline text-blue-500" onClick={() => {
          router.push(ROUTE_PATHS.VALIDATE_EMAIL);
          closeModal();
        }}>
              {tChat("validateEmail")}
            </span>
          </div>}
        {isEmailVerified && hasUserAnsweredAfterFirstMessage && <div className={cn("absolute left-1/2 text-foreground-heading text-xs border top-1/2 -translate-x-1/2  z-20 bg-card/80 p-2 rounded-full shadow-md text-center")}>
            {tChat("waitForReply")}
          </div>}
        {messages.map((message, index) => {
        const isReadByOtherUser = sessionData.id === message.senderId && otherUserLastReadAt != null && (firstUnreadByOtherUserIndex === -1 || index < firstUnreadByOtherUserIndex);
        const showSeparatorAfterThis = index === separatorIndex && separatorIndex >= 0 && unreadCount > 0;
        const previousMessage = index > 0 ? messages[index - 1] : null;
        const timeDiff = previousMessage ? new Date(message.createdAt).getTime() - new Date(previousMessage.createdAt).getTime() : Infinity;
        const oneHourInMs = 60 * 60 * 1000;
        const isLastInBlock = index === messages.length - 1 || messages[index + 1].senderId !== message.senderId || index < messages.length - 1 && new Date(messages[index + 1].createdAt).getTime() - new Date(message.createdAt).getTime() > oneHourInMs;
        const isFirstInBlock = index === 0 || messages[index - 1].senderId !== message.senderId || timeDiff > oneHourInMs;

        // Don't show time separator if there's an unread separator right before this message
        const previousMessageHasUnreadSeparator = index > 0 && index - 1 === separatorIndex && unreadCount > 0;
        const showTimeSeparator = isFirstInBlock && !previousMessageHasUnreadSeparator;

        // Show "seen" under last message from user if it's been read and other user hasn't replied yet
        const isUserMessage = sessionData.id === message.senderId;
        const hasOtherUserRepliedAfter = index < messages.length - 1 && messages.slice(index + 1).some(m_1 => m_1.senderId !== sessionData.id);
        const showSeen = isUserMessage && isLastInBlock && isReadByOtherUser && !hasOtherUserRepliedAfter;
        const otherUserName = conversationData?.pages[0].data?.conversation.otherUser.name || user?.name;

        // Prefer the live parent from the loaded list (real-time content and
        // delete state); fall back to the server-sent preview when the parent
        // is outside the loaded page window.
        const repliedToId = message.replyToId ?? message.replyTo?.id ?? null;
        const parentInList = repliedToId ? messages.find(m_2 => m_2.id === repliedToId) : undefined;
        const replyQuote = repliedToId ? (() => {
          const senderId = parentInList?.senderId ?? message.replyTo?.senderId;
          const deleted = parentInList?.deleted ?? message.replyTo?.deleted ?? false;
          const image = deleted ? null : parentInList?.attachments?.[0]?.url ?? message.replyTo?.image ?? null;
          const text = deleted ? tChat("messageDeleted") : parentInList?.content ?? message.replyTo?.content ?? "";
          return {
            author: senderId === sessionData.id ? tChat("you") : otherUserName,
            // Image with no caption → show a "Photo" label next to the thumb.
            content: text || (image ? tChat("photo") : ""),
            image
          };
        })() : undefined;
        const actions: ActionItem[] = [];
        if (!message.deleted) {
          actions.push({
            label: tChat("reply"),
            icon: <MessageSquareReply className="h-4 w-4" />,
            onClick: () => {
              setReplyTo(message);
              // Only auto-focus on desktop. On touch/PWA a programmatic focus
              // flips the keyboard-open state (dropping the bottom safe-area
              // padding) without reliably raising the keyboard.
              if (!isTouch) {
                requestAnimationFrame(() => inputRef.current?.focus());
              }
            }
          });
        }
        if (isUserMessage && !message.deleted) {
          actions.push({
            label: tChat("deleteMessage"),
            icon: <Trash2 className="h-4 w-4" />,
            onClick: () => deleteMessage(message.id),
            variant: "destructive"
          });
        }
        return <React.Fragment key={message.id}>
              {showTimeSeparator && <div className="flex justify-center my-3">
                  <span className="text-xs text-faint-foreground bg-card px-3 py-1 rounded-full">
                    {formatMessageTimestamp(message.createdAt, "pt")}
                  </span>
                </div>}
              <div id={`chat-msg-${message.id}`} className="relative rounded-lg transition-colors">
                <ChatMessage onQuoteClick={repliedToId ? () => scrollToMessage(repliedToId) : undefined} userName={conversationData?.pages[0].data?.conversation.otherUser.name || user?.name} userImg={conversationData?.pages[0].data?.conversation.otherUser.image || user?.image} role={isUserMessage ? "user" : "non-user"} content={message.deleted ? isUserMessage ? tChat("messageDeletedByYou") : tChat("messageDeleted") : message.content} deleted={message.deleted} replyQuote={replyQuote} attachments={(message.attachments ?? []).filter(a => !!a?.url).map(a_0 => ({
              id: a_0.id ?? a_0.url,
              url: a_0.url,
              width: a_0.width,
              height: a_0.height
            }))} status={message.status} actions={actions.length ? actions : undefined} isFirstInBlock={isFirstInBlock} isLastInBlock={isLastInBlock} />
                {showSeen && <span className="absolute -bottom-1 sm:-bottom-1 right-0 text-xs text-faint-foreground">
                    {tChat("seen")}
                  </span>}
              </div>
              {showSeparatorAfterThis && unreadCount > 0 && <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-px bg-border-strong" />
                  <span className="text-xs font-medium text-faint-foreground">
                    {tChat("unread")}
                  </span>
                </div>}
            </React.Fragment>;
      })}

        <div ref={messagesEndRef} />
      </div>
      <div onFocus={onInputFocus} onBlur={onInputBlur} className={cn("shrink-0 w-full pt-2", keyboardOpen ? "pb-2" : "pb-safe")}>
        <form onSubmit={handleSubmit} className="relative">
          {!isAtBottom && <div className="absolute -top-12 left-1/2 -translate-x-1/2">
              <Button variant="simpleDark" onClick={handleScrollToBottom} size="icon" className="rounded-full shadow-lg relative">
                <ChevronDown />
                {newMessagesCount > 0 && <span className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                    {newMessagesCount > 99 ? "99+" : newMessagesCount}
                  </span>}
              </Button>
            </div>}
          {replyTo && <div className="mb-2 flex items-center gap-2 rounded-md border-l-2 border-primary bg-muted/60 px-3 py-2">
              {replyTo.attachments?.[0]?.url && <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={replyTo.attachments[0].url} alt="" className="h-full w-full object-cover" />
                </span>}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-primary">
                  {tChat("replyingTo", {
                name: replyTo.senderId === sessionData.id ? tChat("you") : conversationData?.pages[0].data?.conversation.otherUser.name || user?.name
              })}
                </p>
                <p className="truncate text-xs text-faint-foreground">
                  {replyTo.deleted ? tChat("messageDeleted") : replyTo.content || (replyTo.attachments?.length ? tChat("photo") : "")}
                </p>
              </div>
              <button type="button" onClick={() => setReplyTo(null)} className="shrink-0 text-faint-foreground hover:text-foreground" aria-label={tChat("cancelReply")}>
                <X className="h-4 w-4" />
              </button>
            </div>}
          {pendingImages.length > 0 && <div className="mb-2 flex flex-wrap gap-2">
              {pendingImages.map(img => <div key={img.id} className="relative h-16 w-16 overflow-hidden rounded-lg border border-border">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={img.previewUrl} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeImage(img.id)} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white" aria-label={tChat("cancelReply")}>
                    <X className="h-3 w-3" />
                  </button>
                </div>)}
            </div>}
          <SubmitInput disabled={!isEmailVerified || hasUserAnsweredAfterFirstMessage} inputRef={inputRef} setValue={function (value: string): void {
          setInputValue(value);
        }} value={inputValue} hasAttachments={pendingImages.length > 0} onPickImages={addImages} data-sentry-element="SubmitInput" data-sentry-source-file="UserConversationChat.tsx" />
        </form>
      </div>
    </div>;
}